import { createSelector } from '@reduxjs/toolkit';
import { isNil, pathOr } from 'ramda';

import ooeConstants from '../constants';
import { READ_ONLY_LOCATION_STATUSES, type State } from './reducer';

const READ_ONLY_STATUS_SET = new Set<string>(READ_ONLY_LOCATION_STATUSES);

export const selectUser = (state: { user: State }) => state.user;

export const selectCmtPerms = createSelector(selectUser, (user) => {
  return Object.keys(user?.permissions ?? {});
});

export const selectUserType = createSelector(selectUser, (user) => user.type);

export const isAdminUser = createSelector(selectUserType, (type) => type === ooeConstants.PERMISSIONS.ADMIN);

export const isVcaUser = createSelector(selectCmtPerms, (perms) =>
  [ooeConstants.PERMISSIONS.VCA, ooeConstants.PERMISSIONS.VCA_BYPASS].some((item) => perms.includes(item)),
);
export const showBBRToggle = createSelector(selectCmtPerms, (perms) =>
  [ooeConstants.PERMISSIONS.ADMIN, ooeConstants.PERMISSIONS.VCA_BYPASS].some((item) => perms.includes(item)),
);

export const selectUserName = createSelector(selectUser, (user) => user.name);
export const selectUserEmail = createSelector(selectUser, (user) => user.email);
export const selectUserCfaGuid = createSelector(selectUser, (user) => user.cfaGuid);
export const selectLoadingLocations = createSelector(selectUser, (user) => user.loading.locations);
export const selectLocationNumber = createSelector(selectUser, (user) => user.selectedLocation);
export const selectUserLocationNumbers = createSelector(selectUser, (user) => user.locationNumbers);
export const hasAllZeroLocationNumber = createSelector(
  selectUserLocationNumbers,
  (locationNumbers = []) => locationNumbers[0] === '00000',
);
export const selectUserLocations = createSelector(selectUser, (user) => user.locations);
export const selectLocationDropdownDisabled = createSelector(
  selectUserLocations,
  (locations) => locations.length <= 1,
);
export const selectLocationDetails = createSelector(
  selectUserLocations,
  selectLocationNumber,
  (locationArray, selectedNumber) =>
    locationArray.filter((location) => location.locationNumber === selectedNumber)[0] || {},
);
export const selectLocationTimezone = createSelector(
  selectLocationDetails,
  (locationDetails) => locationDetails.timeZone,
);
export const selectLocationContactDetails = createSelector(
  selectLocationDetails,
  selectLocationNumber,
  (details, locationNumber) => {
    const restaurantName = pathOr('', ['name'], details);
    const restaurantAddress1 = pathOr('', ['locationContact', 'streetAddress', 'address1'], details);
    const restaurantAddress2 = `${pathOr('', ['locationContact', 'streetAddress', 'city'], details)}, ${pathOr('', ['locationContact', 'streetAddress', 'state'], details)} ${pathOr('', ['locationContact', 'streetAddress', 'zipCode', 'zip'], details)}`;
    const restaurantPhone = `${pathOr('', ['locationContact', 'daytimePhone', 'areaCode'], details)}-${pathOr('', ['locationContact', 'daytimePhone', 'phoneNumber'], details)}`;

    let phoneNumber;
    switch (locationNumber) {
      case '03606':
        phoneNumber = ooeConstants.HARDCODED_PHONE_FOR_03606;
        break;
      case '02891':
        phoneNumber = ooeConstants.HARDCODED_PHONE_FOR_02891;
        break;
      case '04302':
        phoneNumber = ooeConstants.HARDCODED_PHONE_FOR_04302;
        break;
      case '03724':
        phoneNumber = ooeConstants.HARDCODED_PHONE_FOR_03724;
        break;
      default:
        phoneNumber = restaurantPhone;
    }

    return {
      restaurantName,
      restaurantAddress1,
      restaurantAddress2,
      restaurantPhone: phoneNumber,
    };
  },
);
export const selectUserHasAgreedToEula = createSelector(selectUser, (user) => user.cmtEula);
export const selectEulaIsLoading = createSelector(selectUser, (user) => user.loading.cmtEula);
export const selectEulaText = createSelector(selectUser, (user) => user.cmtEulaText ?? '');

export const selectLocationMOSettings = createSelector(
  selectLocationDetails,
  (details) => details?.mobileOrderingSettings,
);

export const selectEnrolledInDms = createSelector(selectLocationDetails, (details) => {
  return details?.activeFlags?.includes('enrolledInCateringDms') ?? false;
});

export const selectCateringDeliveryMOSettings = createSelector(
  selectLocationMOSettings,
  (settings) => settings?.fulfillmentMethods?.cateringDelivery,
);

export const selectCateringPickupMOSettings = createSelector(
  selectLocationMOSettings,
  (settings) => settings?.fulfillmentMethods?.cateringPickup,
);

export const selectLocationMOSettingsFulfillmentMethods = createSelector(
  selectLocationMOSettings,
  (settings) => settings?.fulfillmentMethods,
);

export const selectLocationAcceptsDelivery = createSelector(
  selectLocationMOSettingsFulfillmentMethods,
  (settings) => !!settings?.cateringDelivery,
);

export const selectLocationAcceptsPickup = createSelector(
  selectLocationMOSettingsFulfillmentMethods,
  (settings) => !!settings?.cateringPickup,
);

//this is hidden from the UI
export const selectOrderMaxFutureDayCountDelivery = createSelector(
  selectCateringDeliveryMOSettings,
  (settings) => settings?.orderMaxFutureDayCount,
);

//this is hidden from the UI
export const selectOrderMaxFutureDayCountPickup = createSelector(
  selectCateringPickupMOSettings,
  (settings) => settings?.orderMaxFutureDayCount,
);

export const selectDeliveryRange = createSelector(
  selectCateringDeliveryMOSettings,
  (settings) => settings?.maxDeliveryRangeMiles,
);

export const selectDeliveryLeadTime = createSelector(selectCateringDeliveryMOSettings, (settings) => {
  if (isNil(settings?.leadTimeMinutes) || settings?.leadTimeMinutes === 0) {
    return ooeConstants.NO_DELIVERY_LEAD_TIME;
  }
  return `${settings.leadTimeMinutes / 60} hr delivery lead time`;
});

export const selectPickupLeadTime = createSelector(selectCateringPickupMOSettings, (settings) => {
  if (isNil(settings?.leadTimeMinutes) || settings?.leadTimeMinutes === 0) {
    return ooeConstants.NO_PICKUP_LEAD_TIME;
  }
  return `${settings.leadTimeMinutes / 60} hr pickup lead time`;
});

export const selectError = createSelector(selectUser, (user) => user.error);
export const selectBypassBusinessRules = createSelector(selectUser, (user) => user.bypassBusinessRules);

export const doesLocationNumberExist = (state: { user: State }, locationNum: string) => {
  const allLocationNumbers = state.user.locations.map((loc) => loc.locationNumber);
  const existingLocation = allLocationNumbers?.find((num) => num === locationNum);
  return Boolean(existingLocation);
};

export const selectIsDeveloperAudience = createSelector(
  selectUser,
  ({ cfaAud }) => cfaAud === ooeConstants.CONTRACTOR_AUDIENCE || cfaAud === ooeConstants.STAFF_AUDIENCE,
);

export const selectAllStoreNamesAndNumbers = createSelector(
  selectUser,
  selectIsDeveloperAudience,
  (user, isDeveloperAudience) => {
    const allStoreNamesAndNumbers = [...user.storeNamesAndNumbers];
    if (isDeveloperAudience) {
      /**
       * The 204 staging lab is not included in location summary.
       * Developers need access to this lab for testing purposes.
       */
      allStoreNamesAndNumbers.push(ooeConstants.LAB_204_LOCATION);
    }
    return allStoreNamesAndNumbers;
  },
);

export const selectSelectedLocationSummary = createSelector(
  selectAllStoreNamesAndNumbers,
  selectLocationNumber,
  (stores, locationNumber) => stores.find((s) => s.locationNumber === locationNumber),
);

export const selectIsSelectedLocationReadOnly = createSelector(
  selectSelectedLocationSummary,
  (summary) => !!summary && READ_ONLY_STATUS_SET.has(summary.status),
);

export const selectLookupLocationError = createSelector(selectError, (error) => error.locations);

export const selectDefaultLocation = createSelector(selectUser, (user) => user.defaultLocation);
